import useDeleteWatch from '@/api/core/watches/useDeleteWatch';
import { WatcherDTO } from 'src/types/application/WatcherDTO';
import { AvatarWithFallback } from '@/components/users/AvatarWithFallback';
import { cn } from '@/lib/utils';
import { UserCheckIcon, TrashIcon, StarIcon } from '@phosphor-icons/react';
import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

interface WatcherListItemProps {
  watcher: WatcherDTO;
  watchableEntityType: string;
  watchableId: string;
  handleToggleWatchKind: (watcher: WatcherDTO) => void;
  isAssignedSection: boolean;
}

export const WatcherListItem = ({
  watcher,
  watchableEntityType,
  watchableId,
  handleToggleWatchKind,
  isAssignedSection,
}: WatcherListItemProps) => {
  const deleteWatch = useDeleteWatch({
    watchableEntityType: watchableEntityType,
    watchableId: watchableId,
    watchId: watcher.watchId,
  });

  const handleAssignClick = () => {
    if (watcher.watchKind !== 'assigned') {
      handleToggleWatchKind(watcher);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-2 rounded-md gap-3',
        isAssignedSection ? 'hover:bg-blue-50' : 'hover:bg-gray-50',
      )}
    >
      <div className="flex items-center gap-3 flex-shrink min-w-0">
        <AvatarWithFallback user={watcher} size="sm" />
        <div className="flex flex-col min-w-0 flex-shrink">
          <div className="font-medium text-sm truncate" title={watcher.name}>
            {watcher.name}
          </div>
          <div className="text-xs text-gray-500 truncate" title={watcher.email}>
            {watcher.email}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {isAssignedSection ? (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <UserCheckIcon className="h-3 w-3" />
            <span>Assigned</span>
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleAssignClick}
                className="h-7 px-2 text-xs text-gray-600 hover:text-blue-600 hover:bg-blue-50"
              >
                <StarIcon className="h-3 w-3 mr-1" />
                Assign
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Assign to {watcher.name}</p>
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                deleteWatch.mutateAsync({
                  watchableEntityType,
                  watchableId,
                  watchId: watcher.watchId,
                })
              }
              className="h-7 w-7 p-0 text-gray-500 hover:text-red-600 hover:bg-red-50"
              disabled={deleteWatch.isLoading}
            >
              <TrashIcon className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Remove</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
};
